import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  User,
  Heart,
  ShoppingBag,
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  Package,
  ChevronDown,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { api, inr } from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const SUPPORT_LINKS = [
  { label: "Contact Us", to: "/contact" },
  { label: "My Orders", to: "/orders" },
  { label: "Track Order", to: "/orders" },
  { label: "Shipping & Returns", to: "/shop" },
];

export default function Navbar() {
  const { count, subtotal } = useCart();
  const { count: wishCount } = useWishlist();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    api.get("/brands").then(({ data }) => setBrands(data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/shop?search=${encodeURIComponent(q)}` : "/shop");
    setOpen(false);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 border-b border-black/10 bg-[#F9F9F6]/90 backdrop-blur-xl"
      data-testid="navbar"
    >
      {/* Row 1 — logo, search, actions */}
      <div className="mx-auto flex h-[68px] max-w-[1400px] items-center gap-4 px-5 sm:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" data-testid="logo-link">
          <img src="/logo-icon.png" alt="Physio Essential" className="h-9 w-9 object-contain" />
          <span className="hidden font-display text-lg font-extrabold leading-none tracking-tight text-[#0A2540] sm:inline">
            Physio<span className="font-medium text-[#2F80ED]"> essential</span>
          </span>
        </Link>

        {/* Search */}
        <form
          onSubmit={submitSearch}
          className="mx-auto flex w-full max-w-2xl items-center gap-2 rounded-full border border-black/15 bg-white px-5 py-2.5 transition-colors focus-within:border-[#2F80ED]"
          data-testid="search-form"
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for products"
            className="w-full bg-transparent text-sm text-black placeholder:text-black/40 focus:outline-none"
            data-testid="navbar-search-input"
          />
          <button type="submit" aria-label="Search" data-testid="navbar-search-btn" className="text-black/50 hover:text-[#2F80ED]">
            <Search className="h-5 w-5" />
          </button>
        </form>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-3 sm:gap-5">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-black/70 transition-colors hover:text-[#2F80ED]" data-testid="account-menu-trigger" aria-label="Account">
                  <User className="h-[22px] w-[22px]" strokeWidth={1.6} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="truncate px-2 py-1.5 text-xs text-muted-foreground">{user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                  <Package className="mr-2 h-4 w-4" /> My Orders
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button onClick={() => navigate("/login")} className="text-black/70 transition-colors hover:text-[#2F80ED]" data-testid="login-icon-btn" aria-label="Login">
              <User className="h-[22px] w-[22px]" strokeWidth={1.6} />
            </button>
          )}

          {/* Wishlist */}
          <button onClick={() => navigate("/wishlist")} className="relative text-black/70 transition-colors hover:text-[#2F80ED]" data-testid="wishlist-btn" aria-label="Wishlist">
            <Heart className="h-[22px] w-[22px]" strokeWidth={1.6} />
            <span
              className="absolute -right-2 -top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#2F80ED] px-1 font-mono text-[10px] font-bold text-white"
              data-testid="wishlist-count"
            >
              {wishCount}
            </span>
          </button>

          {/* Cart */}
          <button onClick={() => navigate("/cart")} className="flex items-center gap-2 text-black/70 transition-colors hover:text-[#2F80ED]" data-testid="cart-btn" aria-label="Cart">
            <span className="relative">
              <ShoppingBag className="h-[22px] w-[22px]" strokeWidth={1.6} />
              <span
                className="absolute -right-2 -top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#0A2540] px-1 font-mono text-[10px] font-bold text-white"
                data-testid="cart-count"
              >
                {count}
              </span>
            </span>
            <span className="hidden font-mono text-sm font-semibold text-black sm:inline" data-testid="cart-total-label">
              {inr(subtotal)}
            </span>
          </button>

          <button
            onClick={() => setOpen((o) => !o)}
            className="text-black/70 lg:hidden"
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Row 2 — category menu (desktop) */}
      <div className="hidden border-t border-black/10 lg:block">
        <nav className="mx-auto flex max-w-[1400px] items-center gap-9 px-5 py-3 sm:px-8">
          {/* All Categories */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 font-display text-[15px] font-bold text-[#0A2540] outline-none transition-colors hover:text-[#2F80ED]" data-testid="nav-all-categories">
                All Categories <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              {CATEGORIES.map((c) => (
                <DropdownMenuItem
                  key={c.key}
                  onClick={() => navigate(`/shop?category=${c.key}`)}
                  className="flex flex-col items-start gap-0.5 py-2"
                  data-testid={`nav-category-${c.key}`}
                >
                  <span className="text-sm font-medium">{c.label}</span>
                  <span className="font-mono text-[10px] uppercase tracking-overline text-black/40">{c.tagline}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/shop?sort=rating" className="font-display text-[15px] font-bold text-[#0A2540] transition-colors hover:text-[#2F80ED]" data-testid="nav-best-sellers">
            Best Sellers
          </Link>

          {/* Brands */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 font-display text-[15px] font-bold text-[#0A2540] outline-none transition-colors hover:text-[#2F80ED]" data-testid="nav-brands">
                Brands <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuItem onClick={() => navigate("/brands")} data-testid="nav-brand-all" className="font-semibold">
                All Brands →
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {brands.slice(0, 8).map((b) => (
                <DropdownMenuItem
                  key={b.slug}
                  onClick={() => navigate(`/brands/${b.slug}`)}
                  className="flex items-center justify-between"
                  data-testid={`nav-brand-${b.slug}`}
                >
                  <span className="text-sm">{b.name}</span>
                  <span className="font-mono text-[10px] text-black/40">{b.count}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/shop?sort=price_low" className="flex items-center gap-1.5 font-display text-[15px] font-bold text-[#0A2540] transition-colors hover:text-[#2F80ED]" data-testid="nav-offer-zone">
            Offer Zone
          </Link>

          {/* Customer Support */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 font-display text-[15px] font-bold text-[#0A2540] outline-none transition-colors hover:text-[#2F80ED]" data-testid="nav-support">
                Customer Support <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              {SUPPORT_LINKS.map((s) => (
                <DropdownMenuItem
                  key={s.label}
                  onClick={() => (s.href ? (window.location.href = s.href) : navigate(s.to))}
                  data-testid={`nav-support-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-black/10 bg-[#F9F9F6] lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="max-h-[75vh] overflow-y-auto px-5 py-4">
              <Link to="/shop" onClick={() => setOpen(false)} className="block border-b border-black/5 py-3 text-sm font-semibold text-black" data-testid="mobile-nav-shop">
                Shop All
              </Link>
              <Link to="/shop?sort=rating" onClick={() => setOpen(false)} className="block border-b border-black/5 py-3 text-sm font-medium text-black/80" data-testid="mobile-nav-best-sellers">
                Best Sellers
              </Link>
              <Link to="/find-my-formula" onClick={() => setOpen(false)} className="block border-b border-black/5 py-3 text-sm font-semibold text-[#0A2540]" data-testid="mobile-nav-find-formula">
                Find My Recovery Formula
              </Link>
              <Link to="/for-physiotherapists" onClick={() => setOpen(false)} className="block border-b border-black/5 py-3 text-sm font-medium text-black/80" data-testid="mobile-nav-physio">
                For Physiotherapists
              </Link>

              <div className="mt-2 border-b border-black/5 py-3">
                <div className="mb-2 font-mono text-[10px] uppercase tracking-overline text-black/40">Recover By Condition</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  <Link to="/conditions/knee-osteoarthritis" onClick={() => setOpen(false)} className="text-xs text-black/75" data-testid="mobile-nav-cond-knee">Knee OA</Link>
                  <Link to="/conditions/hip-osteoarthritis" onClick={() => setOpen(false)} className="text-xs text-black/75" data-testid="mobile-nav-cond-hip">Hip OA</Link>
                  <Link to="/conditions/shoulder-rehabilitation" onClick={() => setOpen(false)} className="text-xs text-black/75" data-testid="mobile-nav-cond-shoulder">Shoulder</Link>
                  <Link to="/conditions/back-neck-rehabilitation" onClick={() => setOpen(false)} className="text-xs text-black/75" data-testid="mobile-nav-cond-back">Back &amp; Neck</Link>
                  <Link to="/conditions/acl-rehabilitation" onClick={() => setOpen(false)} className="text-xs text-black/75" data-testid="mobile-nav-cond-acl">ACL</Link>
                  <Link to="/conditions/fracture-recovery" onClick={() => setOpen(false)} className="text-xs text-black/75" data-testid="mobile-nav-cond-fracture">Fracture</Link>
                  <Link to="/conditions/sports-recovery" onClick={() => setOpen(false)} className="text-xs text-black/75" data-testid="mobile-nav-cond-sports">Sports Recovery</Link>
                  <Link to="/conditions/geriatric-mobility" onClick={() => setOpen(false)} className="text-xs text-black/75" data-testid="mobile-nav-cond-geriatric">Healthy Aging</Link>
                </div>
              </div>

              <div className="mt-2 border-b border-black/5 py-3">
                <div className="mb-2 font-mono text-[10px] uppercase tracking-overline text-black/40">Categories</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  {CATEGORIES.map((c) => (
                    <Link
                      key={c.key}
                      to={`/shop?category=${c.key}`}
                      onClick={() => setOpen(false)}
                      className="text-xs text-black/75"
                      data-testid={`mobile-nav-${c.key}`}
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>

              <Link to="/brands" onClick={() => setOpen(false)} className="block border-b border-black/5 py-3 text-sm font-medium text-black/80" data-testid="mobile-nav-brands">
                Brands
              </Link>
              <Link to="/about" onClick={() => setOpen(false)} className="block border-b border-black/5 py-3 text-sm font-medium text-black/80" data-testid="mobile-nav-about">
                About / Our Science
              </Link>
              <Link to="/contact" onClick={() => setOpen(false)} className="block border-b border-black/5 py-3 text-sm font-medium text-black/80" data-testid="mobile-nav-contact">
                Contact Us
              </Link>
              <Link to="/orders" onClick={() => setOpen(false)} className="block py-3 text-sm font-medium text-black/80" data-testid="mobile-nav-orders">
                My Orders
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
